import { useStytch, useStytchSession, useStytchUser } from '@stytch/nextjs';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';

import { MFARecoveryCodeVerification } from 'components/app-components/mfa/MFARecoveryCodeVerification';
import SetUpMFAModal from 'components/app-components/mfa/SetUpMFAModal';
import ValidateOTPModal from 'components/app-components/mfa/ValidateOTPModal';
import theraToast from 'components/generic-components/toast/TheraToast';
import { useUser } from 'context/user';
import { OTP_EXPIRATION_MINUTES } from 'lib/constants';
import { AuthRoutes, isAlwaysPublicRoute } from 'routes';
import { MFAMethodType } from 'types/mfa';
import { logError } from 'utils/error';
import LoadingScreen from '../LoadingScreen';

const routesToIgnore = [AuthRoutes.SuperAdmin, AuthRoutes.Authenticate, AuthRoutes.SignUpAddPaymentMethod];
// https://stytch.com/docs/sdks/resources/mfa

/**
 * 2FA HOC
 *
 * This component is used to handle the 2FA setup and verification for the user.
 * It is used in the app wrapper to ensure that the user is always prompted to set up 2FA if they are not already set up.
 * It is also used to verify the OTP for the user when they are logging in.
 *
 */
const MultiFactorAuthentication = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const [isDeviceStolen, setIsDeviceStolen] = useState(false);
  const [isSendingEmailOTP, setIsSendingEmailOTP] = useState(false);
  const { logout, user, roleType, isLoading: isUserLoading, canSkip2FA, setIs2FAVerified, currentCompany } = useUser();
  const { user: stytchUser, isInitialized } = useStytchUser();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [otpData, setOtpData] = useState({
    showOTPModal: false,
    methodIDForOTP: '',
    emailForOTP: '',
    phoneNumberForOTP: '',
    mfaType: '',
  });
  const { session } = useStytchSession();
  const stytch = useStytch();
  const { email, isEmailVerified, mfaUserDetails } = user || {};
  const userPhoneNumber = stytchUser?.phone_numbers?.[0]?.phone_number;
  const { mfaType } = mfaUserDetails || {};
  const hasUserSetMFA = Boolean(mfaType);
  const isWorkerNotOnboarded = roleType === 'worker' && !user?.isOnboarded;
  const { settings } = currentCompany || {};
  const { enabledApps } = settings || {};
  const isCompanyIncorporated = Boolean(currentCompany?.incorporationDetails);
  const isUserNotOnboarded = !currentCompany
    ? true
    : roleType !== 'worker'
      ? !Object.values(enabledApps || {}).some((value) => value === true) && !isCompanyIncorporated
      : isWorkerNotOnboarded;

  // routes to skip MFA - check if current path includes any routes from routesToIgnore
  const isSkippableRoute = routesToIgnore.some((route) => router.pathname.includes(route));
  // Not folded into isSkippableRoute, which marks 2FA as passed. A public page must render for a
  // visitor whose sign-in is half done, without sending them a code or counting them as verified.
  const isPublicRoute = isAlwaysPublicRoute(router.pathname);
  const shouldAskForOTP = canSkip2FA || isSkippableRoute ? false : hasUserSetMFA;
  // Enrolment is deferred across the whole sign-up flow, matched by prefix so screens added to it
  // later inherit this instead of re-earning the bug. It matters because the component below
  // renders the setup modal *instead of* children: prompting mid-wizard unmounts the wizard, and
  // closing the modal signs the user out. The wizard creates the company part-way through, which
  // flips isUserNotOnboarded to false and fired the prompt over a half-filled form. Users are
  // still prompted to enrol as soon as they reach the app.
  const isSignUpFlow = router.pathname.startsWith(AuthRoutes.SignUp);
  const promptForMFA =
    !hasUserSetMFA && isEmailVerified && !canSkip2FA && !isSkippableRoute && !isSignUpFlow && !isUserNotOnboarded;
  const isSessionComplete = canSkip2FA
    ? true
    : shouldAskForOTP
      ? session?.authentication_factors?.length >= 2
      : session?.authentication_factors?.length === 1;

  const handleOTPInitiation = useCallback(
    async (email: string, mfaType: string, userPhoneNumber: string) => {
      const baseOtpData = {
        showOTPModal: true,
        emailForOTP: email,
        phoneNumberForOTP: userPhoneNumber,
        mfaType,
        methodIDForOTP: null,
      };

      if ((mfaType === MFAMethodType.SMS || mfaType === MFAMethodType.WHATSAPP) && !userPhoneNumber) {
        throw new Error('No phone number found');
      }

      try {
        let res;
        if (mfaType === MFAMethodType.EMAIL) {
          res = await stytch.otps.email.send(email, {
            expiration_minutes: OTP_EXPIRATION_MINUTES,
          });
          setIsSendingEmailOTP(false);
        } else if (mfaType === MFAMethodType.SMS) {
          res = await stytch.otps.sms.send(userPhoneNumber, {
            expiration_minutes: OTP_EXPIRATION_MINUTES,
          });
        } else if (mfaType === MFAMethodType.WHATSAPP) {
          res = await stytch.otps.whatsapp.send(userPhoneNumber, {
            expiration_minutes: OTP_EXPIRATION_MINUTES,
          });
        }
        if (mfaType !== MFAMethodType.TOTP) {
          baseOtpData.methodIDForOTP = res.method_id;
          theraToast.success(
            `OTP sent to ${mfaType === MFAMethodType.EMAIL ? 'email address linked to your Thera account' : mfaType === MFAMethodType.WHATSAPP ? 'your WhatsApp' : 'your phone number'}.`,
            {
              id: `otp-sent`,
            },
          );
        }
      } catch (error) {
        const httpStatusCode = error?.status_code;
        if (mfaType === MFAMethodType.EMAIL) {
          baseOtpData.mfaType = mfaType;
          setIsSendingEmailOTP(false);
        }
        const toastMessage =
          httpStatusCode === 429
            ? 'Too many requests. Please try again later in some time or Contact Support.'
            : 'Failed to send OTP. Please try again.';
        theraToast.error({
          message: toastMessage,
          error: error,
          options: {
            id: 'otp-send-failed',
          },
        });
        return;
      }
      setOtpData(baseOtpData);
    },
    [stytch],
  );

  useEffect(() => {
    if (!isInitialized || isPublicRoute) return;
    if (!isSessionComplete && isEmailVerified && shouldAskForOTP && !isSkippableRoute) {
      setIsAuthenticated(false);
      setIs2FAVerified(false);
      handleOTPInitiation(email, mfaType, userPhoneNumber);
      return;
    }
    setIsAuthenticated(true);
    setIs2FAVerified(true);
    // handleOTPInitiation, email, mfaType and userPhoneNumber are deliberately omitted. They are
    // the payload of the challenge, not a reason to re-run it: adding them re-sends an OTP every
    // time the profile refetches, which is once a minute on this screen. The deps listed are the
    // conditions that decide whether a challenge is owed at all.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, isSessionComplete, isEmailVerified, shouldAskForOTP, isSkippableRoute, isInitialized, isPublicRoute]);

  const clearOTPData = useCallback(() => {
    setOtpData({
      showOTPModal: false,
      methodIDForOTP: '',
      emailForOTP: '',
      mfaType: '',
      phoneNumberForOTP: '',
    });
    setIsAuthenticated(true);
    setIs2FAVerified(true);
  }, [setIs2FAVerified]);

  const handleOTPModalClose = useCallback(
    (isLoggingIn?: boolean) => {
      if (isLoggingIn) {
        clearOTPData();
        return;
      }
      logout();
    },
    [clearOTPData, logout],
  );

  const handleUnableToReceiveCode = useCallback(async () => {
    const currentMethod =
      otpData.mfaType === MFAMethodType.TOTP
        ? 'Authenticator App'
        : otpData.mfaType === MFAMethodType.SMS
          ? 'SMS'
          : 'WhatsApp';
    const error = new Error(`User is unable to receive ${currentMethod} OTP, opting for email OTP instead.`);
    logError(error, {
      source: 'MFA',
      isToastError: false,
      data: { email, userPhoneNumber, mfaType, userID: user?.userID },
    });
    setIsSendingEmailOTP(true);
    handleOTPInitiation(email, MFAMethodType.EMAIL, userPhoneNumber);
  }, [email, userPhoneNumber, mfaType, user, handleOTPInitiation, otpData.mfaType]);

  const handleMFASetupModalClose = useCallback(() => {
    clearOTPData();
    logout();
  }, [clearOTPData, logout]);

  if (isPublicRoute) {
    return children;
  }

  if (isUserLoading || !router.isReady) {
    return <LoadingScreen />;
  }

  // Do not prompt for MFA setup if the user has not onboarded or email is not verified
  if (!isEmailVerified || isWorkerNotOnboarded) {
    return children;
  }

  if (promptForMFA) {
    return (
      <SetUpMFAModal
        open={promptForMFA}
        onClose={handleMFASetupModalClose}
        email={email}
        shouldShowLogoutConfirmation={true}
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        {otpData.showOTPModal && !isDeviceStolen && (
          <ValidateOTPModal
            open={otpData.showOTPModal}
            onClose={handleOTPModalClose}
            onSuccess={clearOTPData}
            methodID={otpData.methodIDForOTP}
            email={otpData.emailForOTP}
            userPhoneNumber={otpData.phoneNumberForOTP}
            selectedMethod={otpData.mfaType}
            isDarkMode
            isSignInValidationModal
            onDeviceStolen={() => {
              console.warn('onDeviceStolen');
              setIsDeviceStolen(true);
            }}
            onUnableToReceiveCode={handleUnableToReceiveCode}
            isSendingEmailOTP={isSendingEmailOTP}
          />
        )}
        {isDeviceStolen && (
          <MFARecoveryCodeVerification open={isDeviceStolen} onClose={() => setIsDeviceStolen(false)} />
        )}
      </>
    );
  }

  return children;
};

export default MultiFactorAuthentication;
